import { useStrings } from "@1js/localization-components";
import { PromptCommand } from "@1js/pl-card-components";
import {
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../../hooks/useFlightContext";
import { MakeItYourOwnLabel } from "./MakeItYourOwn.web.string";

const useStyles = makeStyles({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    alignSelf: "stretch",
    paddingTop: "32px",
    ...shorthands.gap("12px"),
    "@media (max-width: 540px)": {
      paddingTop: "24px",
    },
  },

  titleContainer: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
  },

  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: "1.5rem",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "2rem",
  },

  smallerTitleOnMobile: {
    "@media (max-width: 540px)": {
      fontSize: "1.25rem",
      lineHeight: "normal",
      letterSpacing: "-0.0375rem",
    },
  },

  goBoldTitle: {
    fontSize: "1.125rem",
  },

  commandContainer: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    backgroundColor: tokens.colorNeutralBackground2,
    fontSize: "1.125rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
    ...shorthands.padding("24px"),
    ...shorthands.gap("24px"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1px", "solid", tokens.colorTransparentStroke),
  },

  smallerCommandContainerOnMobile: {
    "@media (max-width: 540px)": {
      fontSize: "1rem",
      flexDirection: "column",
      alignItems: "flex-end",
    },
  },

  goBoldCommandContainer: {
    maxWidth: "484px",
  },

  promptCommand: {
    fontSize: tokens.fontSizeBase300,
  },
});

export const MakeItYourOwn: React.FC<{ text: string }> = ({ text }) => {
  const styles = useStyles();
  const localizations = useStrings({
    MakeItYourOwnLabel,
  });
  const { isFlightActive } = useFlightContext();
  const isGoBoldEnabled = isFlightActive("isGoBoldPromptCardEnabled");

  return (
    <div className={styles.mainContainer} data-testid="makeityourown">
      <div className={styles.titleContainer}>
        <Text
          className={mergeClasses(
            styles.title,
            isGoBoldEnabled ? styles.goBoldTitle : styles.smallerTitleOnMobile
          )}
        >
          {localizations.MakeItYourOwnLabel()}
        </Text>
      </div>
      <div
        className={mergeClasses(
          styles.commandContainer,
          isGoBoldEnabled
            ? styles.goBoldCommandContainer
            : styles.smallerCommandContainerOnMobile
        )}
      >
        <PromptCommand
          command={text}
          appearance="web"
          className={isGoBoldEnabled ? styles.promptCommand : undefined}
        />
      </div>
    </div>
  );
};
